/* ============================================================================
   Puentes React para el motor de resortes + utilidades de gesto.
   ========================================================================== */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { SPRING, SpringValue, prefersReducedMotion, type SpringConfig } from './spring'

/**
 * Devuelve un número animado por resorte hacia `target`.
 * Re-apunta en vuelo conservando velocidad: cambiar `target` a media animación
 * no produce salto.
 *
 * `immediate` salta al valor sin animar. Es lo correcto mientras el lector
 * arrastra el cursor sobre una gráfica: ahí el número debe seguir al dedo con
 * exactitud, no perseguirlo. Interpolar durante un scrub muestra cifras que no
 * corresponden a ningún mes.
 */
export function useSpringNumber(
  target: number,
  config: SpringConfig = SPRING.data,
  immediate = false,
): number {
  const springRef = useRef<SpringValue | null>(null)
  if (springRef.current === null) springRef.current = new SpringValue(target, config)
  const spring = springRef.current

  const [value, setValue] = useState(() => spring.value)

  useEffect(() => {
    const unsubscribe = spring.onChange((v) => setValue(v))
    return unsubscribe
  }, [spring])

  useEffect(() => {
    if (immediate) spring.set(target)
    else spring.to(target, config)
  }, [spring, target, config, immediate])

  useEffect(() => () => spring.destroy(), [spring])

  return value
}

/** Acceso imperativo a un resorte (para escribir atributos SVG sin re-render). */
export function useSpring(initial: number, config: SpringConfig = SPRING.ui): SpringValue {
  const ref = useRef<SpringValue | null>(null)
  if (ref.current === null) ref.current = new SpringValue(initial, config)
  useEffect(() => {
    const s = ref.current
    return () => s?.destroy()
  }, [])
  return ref.current
}

/**
 * Interpolación de progreso 0→1 al montar, para entradas escalonadas.
 * Con movimiento reducido entrega 1 de inmediato (cross-fade, no deslizamiento).
 */
export function useEnter(delayMs = 0): number {
  const [p, setP] = useState(() => (prefersReducedMotion() ? 1 : 0))
  useEffect(() => {
    if (prefersReducedMotion()) {
      setP(1)
      return
    }
    let raf = 0
    const id = window.setTimeout(() => {
      raf = requestAnimationFrame(() => setP(1))
    }, delayMs)
    return () => {
      window.clearTimeout(id)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [delayMs])
  return p
}

/* --- Velocidad ----------------------------------------------------------- */

interface Sample {
  v: number
  t: number
}

/**
 * Historial corto de posición/tiempo. La velocidad de suelta se toma de los
 * últimos ~80 ms: un solo par de puntos es ruidoso y produce flicks erráticos.
 */
export class VelocityTracker {
  private samples: Sample[] = []
  private window: number

  constructor(windowMs = 80) {
    this.window = windowMs
  }

  reset(value: number, time = performance.now()): void {
    this.samples = [{ v: value, t: time }]
  }

  add(value: number, time = performance.now()): void {
    this.samples.push({ v: value, t: time })
    const cutoff = time - this.window * 2
    while (this.samples.length > 2 && this.samples[0]!.t < cutoff) this.samples.shift()
  }

  /** px/s. 0 si el puntero llevaba quieto. */
  velocity(now = performance.now()): number {
    if (this.samples.length < 2) return 0
    const last = this.samples[this.samples.length - 1]!
    // Si el último movimiento es viejo, el dedo se detuvo: no hay inercia.
    if (now - last.t > this.window) return 0
    let first = this.samples[0]!
    for (let i = this.samples.length - 1; i >= 0; i--) {
      const s = this.samples[i]!
      first = s
      if (last.t - s.t >= this.window) break
    }
    const dt = (last.t - first.t) / 1000
    if (dt <= 0.001) return 0
    return (last.v - first.v) / dt
  }
}

/* --- Arrastre 1:1 -------------------------------------------------------- */

export interface DragState {
  /** Desplazamiento desde el punto de agarre, en px. */
  delta: number
  /** Velocidad instantánea, px/s. */
  velocity: number
  /** Coordenada del puntero en el eje activo. */
  position: number
}

export interface DragOptions {
  axis?: 'x' | 'y'
  /** px de histéresis antes de comprometerse a la dirección. */
  threshold?: number
  onStart?: (state: DragState) => void
  onMove?: (state: DragState) => void
  onEnd?: (state: DragState) => void
}

/**
 * Arrastre 1:1 con Pointer Events y `setPointerCapture`, respetando el offset
 * de agarre. El seguimiento continúa aunque el puntero salga del elemento.
 */
export function useDrag<T extends HTMLElement>(options: DragOptions) {
  const ref = useRef<T | null>(null)
  const opts = useRef(options)
  opts.current = options

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const axis = opts.current.axis ?? 'y'
    const threshold = opts.current.threshold ?? 10
    const tracker = new VelocityTracker()
    let pointerId: number | null = null
    let origin = 0
    let committed = false

    const coord = (e: PointerEvent) => (axis === 'y' ? e.clientY : e.clientX)

    const onDown = (e: PointerEvent) => {
      if (pointerId !== null) return
      if (e.pointerType === 'mouse' && e.button !== 0) return
      pointerId = e.pointerId
      origin = coord(e)
      committed = threshold === 0
      tracker.reset(origin, e.timeStamp)
      el.setPointerCapture(e.pointerId)
      if (committed) {
        opts.current.onStart?.({ delta: 0, velocity: 0, position: origin })
      }
    }

    const onMove = (e: PointerEvent) => {
      if (pointerId !== e.pointerId) return
      const pos = coord(e)
      tracker.add(pos, e.timeStamp)
      const delta = pos - origin
      if (!committed) {
        if (Math.abs(delta) < threshold) return
        committed = true
        // Reancla para que no salte los px de histéresis.
        origin = pos - Math.sign(delta) * threshold
        opts.current.onStart?.({ delta: pos - origin, velocity: 0, position: pos })
      }
      opts.current.onMove?.({
        delta: pos - origin,
        velocity: tracker.velocity(e.timeStamp),
        position: pos,
      })
    }

    const finish = (e: PointerEvent) => {
      if (pointerId !== e.pointerId) return
      const pos = coord(e)
      const wasCommitted = committed
      pointerId = null
      committed = false
      if (el.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId)
      if (wasCommitted) {
        opts.current.onEnd?.({
          delta: pos - origin,
          velocity: tracker.velocity(e.timeStamp),
          position: pos,
        })
      }
    }

    el.addEventListener('pointerdown', onDown)
    el.addEventListener('pointermove', onMove)
    el.addEventListener('pointerup', finish)
    el.addEventListener('pointercancel', finish)
    return () => {
      el.removeEventListener('pointerdown', onDown)
      el.removeEventListener('pointermove', onMove)
      el.removeEventListener('pointerup', finish)
      el.removeEventListener('pointercancel', finish)
    }
  }, [])

  return ref
}

/* --- Medición ------------------------------------------------------------ */

export interface Size {
  width: number
  height: number
}

/**
 * Ancho/alto observados del elemento. Las gráficas se dimensionan con esto.
 *
 * Mide de tres formas a propósito:
 *
 * 1. `useLayoutEffect` con `getBoundingClientRect` ANTES del primer pintado. Sin
 *    esto, el primer render usaba un ancho de reserva que en un teléfono era más
 *    del doble del contenedor: el SVG se salía y ensanchaba la página entera
 *    hasta que el observer disparaba.
 * 2. `ResizeObserver` para los cambios de layout.
 * 3. El evento `resize` de la ventana como red de seguridad: el callback del
 *    ResizeObserver va atado al ciclo de render, y cuando el navegador lo frena
 *    —pestaña en segundo plano, ventana sin foco— puede no llegar nunca.
 */
export function useSize<T extends Element>(): [React.RefObject<T | null>, Size] {
  const ref = useRef<T | null>(null)
  const [size, setSize] = useState<Size>({ width: 0, height: 0 })

  const measure = useCallback(() => {
    const el = ref.current
    if (!el) return
    const box = el.getBoundingClientRect()
    setSize((prev) =>
      Math.abs(prev.width - box.width) < 0.5 && Math.abs(prev.height - box.height) < 0.5
        ? prev
        : { width: box.width, height: box.height },
    )
  }, [])

  /* Sin arreglo de dependencias: mide en CADA commit. Es una lectura de layout
     barata y el setState esta guardado contra valores iguales, asi que no hay
     bucle; a cambio, el ancho se auto-corrige aunque ni el ResizeObserver ni el
     evento resize lleguen a dispararse. */
  useLayoutEffect(() => {
    measure()
  })

  useEffect(() => {
    const el = ref.current
    if (!el) return

    let ro: ResizeObserver | null = null
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => measure())
      ro.observe(el)
    }
    window.addEventListener('resize', measure)
    return () => {
      ro?.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [measure])

  return [ref, size]
}

/** `true` cuando el viewport cumple la media query. */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false,
  )
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const mq = window.matchMedia(query)
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches)
    setMatches(mq.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [query])
  return matches
}

/** Config de resorte estable para pasar a `useSpringNumber` sin re-crear. */
export function useSpringConfig(config: SpringConfig): SpringConfig {
  const { damping, response } = config
  return useMemo(() => ({ damping, response }), [damping, response])
}
